import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { LayoutChangeEvent } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';

import { SizedBox } from '@/components/SizedBox';
import { showScrollIndicator } from '@/utils/constants-platform-specific';

import { Button } from '../ButtonComponent';
import { Box, Column, Row } from '../lib/components';
import { MatchupColumn } from './MatchupColumn';
import { ScoreColumn, ScoreColumnTheme, ScoreEmphasis } from './ScoreColumn';
import { TIEBREAKER_OFFSET, getTiebreakerLayout, getTiebreakerSize } from './TiebreakerScore';
import { PeriodScore, ScoreBoardGridProps, ScoreBoardVariant } from './types';

const SHARED = {
    outerPadding: 16,
    scoreBreakdownMinWidth: 88,
    columnAlign: 'center' as const,
    primaryColumnSpacing: 's4' as const,
};

const SIZE = {
    medium: {
        gameInfoHeight: 16,
        gameInfoVariant: 'bodyXSmall' as const,
        gameInfoStatusVariant: 'labelMedium' as const,
        teamNameVariant: 'titleSmall' as const,
        periodLabelVariant: 'labelMedium' as const,
        scoreVariant: 'bodySmall' as const,
        totalScoreVariant: 'titleSmall' as const,
        subScoreVariant: 'bodyXSmall' as const,
        innerPadding: 's8' as const,
        primaryColumnPaddingLeft: 's4' as const,
        logoWidth: 20,
        logoHeight: 20,
        sizedBoxDimensions: { top: 6, middle: 4 },
        columnGap: 2,
    },
    large: {
        gameInfoHeight: 20,
        gameInfoVariant: 'bodySmall' as const,
        gameInfoStatusVariant: 'labelLarge' as const,
        teamNameVariant: 'titleMedium' as const,
        periodLabelVariant: 'labelLarge' as const,
        scoreVariant: 'bodyMedium' as const,
        totalScoreVariant: 'titleMedium' as const,
        subScoreVariant: 'bodySmall' as const,
        innerPadding: 's12' as const,
        primaryColumnPaddingLeft: 's8' as const,
        logoWidth: 24,
        logoHeight: 24,
        sizedBoxDimensions: { top: 10, middle: 8 },
        columnGap: 2,
    },
};

const CONDENSED_BOX_COL_WIDTH = 20;

const spec = {
    classicMedium: {
        ...SHARED,
        ...SIZE.medium,
        boxColWidth: 24,
    },
    classicLarge: {
        ...SHARED,
        ...SIZE.large,
        boxColWidth: 28,
        primaryColumnSpacing: 's6' as const,
    },
    classicCondensedMedium: {
        ...SHARED,
        ...SIZE.medium,
        boxColWidth: CONDENSED_BOX_COL_WIDTH,
    },
    classicCondensedLarge: {
        ...SHARED,
        ...SIZE.large,
        boxColWidth: CONDENSED_BOX_COL_WIDTH,
        primaryColumnSpacing: 's6' as const,
    },
    modernMedium: {
        ...SHARED,
        ...SIZE.medium,
        boxColWidth: CONDENSED_BOX_COL_WIDTH,
        tiebreakerScoreOffset: { right: 4, up: 4 },
    },
    modernLarge: {
        ...SHARED,
        ...SIZE.large,
        boxColWidth: CONDENSED_BOX_COL_WIDTH,
        columnGap: 6,
        tiebreakerScoreOffset: TIEBREAKER_OFFSET,
    },
} satisfies Record<ScoreBoardVariant, unknown>;

const isModernVariant = (v: ScoreBoardVariant) => v === 'modernMedium' || v === 'modernLarge';

export const ScoreBoardGrid = ({
    team1,
    team2,
    team1Indicator,
    team2Indicator,
    breakdown,
    backgroundColor,
    finalScoreColumns,
    gameScoreColumn,
    matchDetails,
    periodDetails,
    activePeriodIndex,
    variant = 'classicLarge',
    eventState,
    onViewFullEvent,
}: ScoreBoardGridProps) => {
    const chosenSpec = spec[variant];
    const columnWidth = chosenSpec.boxColWidth;
    const columnGap = chosenSpec.columnGap;
    const tiebreakerScoreOffset = 'tiebreakerScoreOffset' in chosenSpec ? chosenSpec.tiebreakerScoreOffset : undefined;
    const isPreEvent = eventState === 'pregame';
    const isLive = eventState === 'live';
    const isModern = isModernVariant(variant);
    const isModernLive = isModern && eventState !== 'final';
    const isModernFinal = isModern && eventState === 'final';

    const columnTheme = useMemo<ScoreColumnTheme>(
        () => ({
            minWidth: chosenSpec.boxColWidth,
            columnAlign: chosenSpec.columnAlign,
            sizedBoxDimensions: chosenSpec.sizedBoxDimensions,
            periodLabelVariant: chosenSpec.periodLabelVariant,
            scoreVariant: chosenSpec.scoreVariant,
            totalScoreVariant: chosenSpec.totalScoreVariant,
            subScoreVariant: chosenSpec.subScoreVariant,
            tiebreakerScoreOffset,
        }),
        [chosenSpec, tiebreakerScoreOffset]
    );

    const getPeriodLayout = useCallback(
        (period: PeriodScore) =>
            getTiebreakerLayout(
                tiebreakerScoreOffset ? getTiebreakerSize(period.team1SubScore, period.team2SubScore) : 'none',
                tiebreakerScoreOffset ?? TIEBREAKER_OFFSET,
                columnGap
            ),
        [tiebreakerScoreOffset, columnGap]
    );

    const getGapAfter = useCallback((period: PeriodScore) => getPeriodLayout(period).gapAfter, [getPeriodLayout]);

    const columnOffsets = useMemo(() => {
        const offsets: number[] = [];
        let offset = 0;
        for (const period of breakdown) {
            offsets.push(offset);
            offset += columnWidth + getGapAfter(period);
        }
        return offsets;
    }, [breakdown, columnWidth, getGapAfter]);

    const lastPeriod = breakdown[breakdown.length - 1];
    const trailingSpace = lastPeriod ? getPeriodLayout(lastPeriod).overhang : 0;

    const ListGap = useCallback(
        ({ leadingItem }: { leadingItem: PeriodScore }) => <SizedBox value={getGapAfter(leadingItem)} />,
        [getGapAfter]
    );

    const renderItem = useCallback(
        ({ item, index }: { item: PeriodScore; index: number }) => {
            const emphasis: ScoreEmphasis = isModernFinal
                ? 'winner'
                : isModernLive && index === activePeriodIndex
                ? 'active'
                : 'normal';
            return (
                <ScoreColumn
                    {...item}
                    periodLabel={item.label ?? item.periodNumber ?? ''}
                    theme={columnTheme}
                    emphasis={emphasis}
                />
            );
        },
        [columnTheme, isModernLive, isModernFinal, activePeriodIndex]
    );

    const getItemLayout = useCallback(
        (_: ArrayLike<PeriodScore> | null | undefined, index: number) => ({
            length: columnWidth,
            offset: columnOffsets[index] ?? 0,
            index,
        }),
        [columnWidth, columnOffsets]
    );

    // Layout-driven scroll setup. Refs hold widths needed only for the one-shot initial scroll.
    const flatListRef = useRef<FlatList<PeriodScore>>(null);
    const hasInitialized = useRef(false);
    const contentWidthRef = useRef(0);
    const containerWidthRef = useRef(0);
    // Read via a ref: breakdown is a new array every live tick, which would re-fire the animated scroll effect.
    const columnLayoutRef = useRef({ columnOffsets, trailingSpace });
    columnLayoutRef.current = { columnOffsets, trailingSpace };

    const [layoutWidths, setLayoutWidths] = useState({ row: 0, results: 0 });

    const getTargetScrollOffset = useCallback(
        (length: number, currentActivePeriodIndex: number | undefined) => {
            if (!length) return null;
            const layout = columnLayoutRef.current;
            const fallbackContentWidth = (layout.columnOffsets[length - 1] ?? 0) + columnWidth + layout.trailingSpace;
            const contentWidth = Math.max(contentWidthRef.current, fallbackContentWidth);
            const containerWidth = containerWidthRef.current;
            if (!contentWidth || !containerWidth) return null;

            const maxOffset = Math.max(0, contentWidth - containerWidth);
            if (!isLive || currentActivePeriodIndex == null || currentActivePeriodIndex >= length) {
                return { offset: maxOffset };
            }

            const revealOffset = (layout.columnOffsets[currentActivePeriodIndex] ?? 0) + columnWidth - containerWidth;
            const offset = Math.min(maxOffset, Math.max(0, revealOffset));
            return { offset };
        },
        [columnWidth, isLive]
    );

    const tryInitialScroll = useCallback(() => {
        if (hasInitialized.current) return;
        const scrollPosition = getTargetScrollOffset(breakdown.length, activePeriodIndex);
        if (!scrollPosition) return;
        hasInitialized.current = true;

        requestAnimationFrame(() => {
            flatListRef.current?.scrollToOffset({ animated: false, offset: scrollPosition.offset });
        });
    }, [activePeriodIndex, breakdown.length, getTargetScrollOffset]);

    // Live period change after init
    useEffect(() => {
        if (!hasInitialized.current || !isLive || activePeriodIndex == null || activePeriodIndex >= breakdown.length)
            return;
        const scrollPosition = getTargetScrollOffset(breakdown.length, activePeriodIndex);
        if (!scrollPosition) return;
        flatListRef.current?.scrollToOffset({ animated: true, offset: scrollPosition.offset });
    }, [isLive, activePeriodIndex, breakdown.length, getTargetScrollOffset]);

    const handleContentSizeChange = useCallback(
        (width: number) => {
            if (contentWidthRef.current !== width) {
                contentWidthRef.current = width;
                hasInitialized.current = false;
            }
            tryInitialScroll();
        },
        [tryInitialScroll]
    );

    const handleListLayout = useCallback(
        (event: LayoutChangeEvent) => {
            const width = event.nativeEvent.layout.width;
            if (containerWidthRef.current !== width) {
                containerWidthRef.current = width;
                hasInitialized.current = false;
            }
            tryInitialScroll();
        },
        [tryInitialScroll]
    );

    const handleResultsLayout = useCallback((event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;
        setLayoutWidths(prev => (prev.results === width ? prev : { ...prev, results: width }));
    }, []);

    const handleRowLayout = useCallback((event: LayoutChangeEvent) => {
        const width = event.nativeEvent.layout.width;
        setLayoutWidths(prev => (prev.row === width ? prev : { ...prev, row: width }));
    }, []);

    const renderScoreColumns = (getPeriodLabel: (p: PeriodScore) => string, columnAlign?: 'center' | 'flex-end') => {
        const length = finalScoreColumns?.length ?? 0;
        return finalScoreColumns?.map((period, index) => (
            <Box
                paddingRight={index !== length - 1 ? chosenSpec.primaryColumnSpacing : 's0'}
                key={period.label ?? period.periodNumber ?? String(index)}
            >
                <ScoreColumn
                    {...period}
                    periodLabel={getPeriodLabel(period)}
                    theme={{ ...columnTheme, ...(columnAlign && { columnAlign }) }}
                    emphasis="total"
                />
            </Box>
        ));
    };

    const showGameScoreColumn = !!gameScoreColumn && !isModernFinal;
    const showFinalScoreColumns = !isModern;
    // onLayout never fires on unmount, so a results column that has gone away keeps its last width.
    const resultsWidth = !isPreEvent && (showGameScoreColumn || showFinalScoreColumns) ? layoutWidths.results : 0;
    const matchupMaxWidth =
        layoutWidths.row - resultsWidth - 2 * chosenSpec.outerPadding - chosenSpec.scoreBreakdownMinWidth;

    return (
        <Column maxWidth="100%" minWidth="100%" backgroundColor={backgroundColor}>
            <Row onLayout={handleRowLayout} style={{ padding: chosenSpec.outerPadding }}>
                <MatchupColumn
                    team1={team1}
                    team2={team2}
                    team1Indicator={team1Indicator}
                    team2Indicator={team2Indicator}
                    theme={chosenSpec}
                    matchDetails={matchDetails}
                    periodDetails={periodDetails}
                    maxWidth={matchupMaxWidth}
                    isLive={isLive}
                />
                {!isPreEvent ? (
                    <>
                        <Box flex={1} />
                        {showGameScoreColumn ? (
                            <Row paddingRight={isModern ? 's8' : 's0'} onLayout={handleResultsLayout}>
                                <ScoreColumn
                                    {...gameScoreColumn}
                                    periodLabel=" "
                                    theme={{ ...columnTheme, columnAlign: 'flex-end' }}
                                    emphasis="total"
                                />
                            </Row>
                        ) : null}
                        <Box overflow="hidden" flexShrink={10}>
                            <Column onLayout={handleListLayout}>
                                <FlatList<PeriodScore>
                                    ref={flatListRef}
                                    horizontal
                                    showsHorizontalScrollIndicator={showScrollIndicator}
                                    data={breakdown}
                                    ListFooterComponent={<SizedBox value={trailingSpace} />}
                                    ItemSeparatorComponent={ListGap}
                                    renderItem={renderItem}
                                    getItemLayout={getItemLayout}
                                    onContentSizeChange={handleContentSizeChange}
                                />
                            </Column>
                        </Box>
                        {showFinalScoreColumns ? (
                            // Gap between the secondary period breakdown and the primary T/R/H column(s).
                            // (8px Large, 4px Medium).
                            <Row paddingLeft={chosenSpec.primaryColumnPaddingLeft} onLayout={handleResultsLayout}>
                                {renderScoreColumns(p => `${p.periodNumber}`)}
                            </Row>
                        ) : null}
                    </>
                ) : null}
            </Row>
            {onViewFullEvent ? (
                <Box style={{ paddingHorizontal: chosenSpec.outerPadding }}>
                    <Button label={'View full event'} hierarchy={'secondary'} onPress={onViewFullEvent} size={'s'} />
                </Box>
            ) : null}
        </Column>
    );
};
